import * as React from 'react';
import {
    ScheduledTournamentConfig,
    TournamentConstraints,
    TournamentSchedule,
    TimeZone,
    TournamentScheduleTime,
    BetStrategyType,
    PokerVariant,
    TournamentConfiguration,
    SatelliteConfiguration,
    PrizeBoostConfiguration,
} from '../../../../../generated/protocol';
import {
    ScheduledTournamentService,
    SCHEDULED_TOURNAMENT_SERVICE_NAME,
} from '../../../../service/poker/tournament/ScheduledTournamentService';
import {
    TournamentConstraintsService,
    TOURNAMENT_CONSTRAINTS_SERVICE_NAME,
} from '../../../../service/poker/tournament/TournamentConstraintsService';
import { AppCtx } from '../../../../ioc/ApplicationContext';
import { RouterView,
    LoadingViewState,
} from '../../RouterView';
import { FormListener } from '../../../form/Form';
import { ScheduledTournamentForm } from '../../../poker/tournament/ScheduledTournamentForm';

interface State extends LoadingViewState {
    tournament?: ScheduledTournamentConfig;
    validationMap?: any;
    errorMessage?: string;
    success?: boolean;
    inProgress?: boolean;
    constraints?: TournamentConstraints;
    timeZones?: TimeZone[];
}

const createInitialState = () => {
    return {
        validationMap: {},
        tournament: {
            configuration: {
                guaranteedPrizePool: 0,
                variant: 'TEXAS_HOLDEM' as PokerVariant,
                betStrategy: 'NO_LIMIT' as BetStrategyType,
                synchronizeBreaks: false,
                lateRegistrationLevel: -1,
                seatsPerTable: 9,
                rebuyConfiguration: {
                    numberOfRebuysAllowed: 0,
                    rebuyAndAddonTimeout: 20000,
                },
                dummyTournament: false,
                satelliteToDummy: false,
                hasBounty: false,
                bountyFee: 0,
                progressivePercentage: 0,
                hasMysteryBounty: false,
                mysteryBountyFee: 0,
            },
            satelliteConfiguration: {
                isSatellite: false,
                isTarget: false,
                satellite: {
                    mustPlay: false,
                    returnAmount: null,
                    tickets: [],
                    preventRegistrationIfRegisteredToTarget: false,
                },
                target: {
                    allowsDirectBuyIn: false,
                    tickets: [],
                },
            },

            allKnownTickets: [],

            schedule: {
                timeZoneId: 'UTC',
                cronSchedule: '0 0 * * * ?',
            },

            handForHandConfiguration: {
                enabled: false,
                tableSizeBasedLimits: [2],
                bubbleBasedLimit: 2,
            },

            prizeBoostConfiguration: {
                enabled: false,
                boostCash: 0,
                tickets: [],
                ticketCount: 0
            },
        },
    };
};

interface Props {
    params: { id: string, copyTemplate?: string }
}

export class ScheduledTournamentView extends RouterView<Props, State> implements FormListener<ScheduledTournamentConfig> {

    tournamentService: ScheduledTournamentService;
    constraintsService: TournamentConstraintsService;

    constructor(props) {
        super(props);
        this.state = Object.assign({}, this.state, createInitialState());
        this.tournamentService = AppCtx.resolve<ScheduledTournamentService>(SCHEDULED_TOURNAMENT_SERVICE_NAME);
        this.constraintsService = AppCtx.resolve<TournamentConstraintsService>(TOURNAMENT_CONSTRAINTS_SERVICE_NAME);
    }

    componentDidUpdate(prevProps: Props, prevState) {
        if (prevProps.params.id !== this.props.params.id) {
            this.refreshContent();
            return;
        }
        super.componentDidUpdate(prevProps, prevState);
    }

    onTournamentSaveConfirmed = () => {
        this.navigate('/tournament/' + this.state.tournament.tournamentTemplateId);
    }

    loadContent(): Promise<any> {
        if (this.props.params.id === 'new') {
            const promises = [];
            promises.push(this.constraintsService.getConstraintsByUser());
            promises.push(this.constraintsService.getTimeZones());
            if (this.props.params.copyTemplate) {
                promises.push(this.tournamentService.getEntity(this.props.params.copyTemplate));
            }
            return Promise.all<any>(promises).then(results => {
                const initialState = createInitialState();
                let tournament: ScheduledTournamentConfig = initialState.tournament;
                if (this.props.params.copyTemplate) {
                    tournament = results[2];
                    tournament.tournamentTemplateId = null;
                    tournament.configuration.id = null;
                    if (tournament.configuration.rebuyConfiguration) {
                        tournament.configuration.rebuyConfiguration.id = null;
                    }
                    tournament.schedule.id = null;
                } else {
                    const configuration = Object.assign({}, tournament.configuration, {operatorIds: [this.state.operatorId]});
                    tournament = Object.assign({}, tournament, {configuration});
                }
                this.setState({
                    constraints: results[0],
                    timeZones: results[1],
                    tournament: tournament,
                    validationMap: {},
                });
            });
        } else {
            return Promise.all<any>([
                this.tournamentService.getEntity(this.props.params.id),
                this.constraintsService.getConstraintsByUser(),
                this.constraintsService.getTimeZones(),
            ]).then(results => {
                this.setState({
                    tournament: results[0],
                    constraints: results[1],
                    timeZones: results[2],
                });
            });
        }

    }

    calculateSchedule = (schedule: TournamentSchedule): Promise<TournamentScheduleTime[]> => {
        return this.constraintsService.calculateSchedule(schedule);
    }

    onSubmit(): Promise<any> {
        const tournament = Object.assign({}, this.state.tournament);
        if (typeof  tournament.handForHandConfiguration.tableSizeBasedLimits === 'string') {
            tournament.handForHandConfiguration.tableSizeBasedLimits = tournament.handForHandConfiguration.tableSizeBasedLimits.split(',');
        }
        return this.tournamentService.save(tournament).then((t: ScheduledTournamentConfig) => {
            this.setState({tournament: t});
        }).catch(err => {
            throw err.message;
        });
    }

    onSubmitError() {
        console.log('State on error = ', this.state.tournament);
    }

    getSatelliteConfiguration(formModel: ScheduledTournamentConfig): SatelliteConfiguration {
        const satelliteConfiguration = {...formModel.satelliteConfiguration};
        const { satelliteToDummy, dummyTournament } = formModel.configuration;
        const { mustPlay } = satelliteConfiguration.satellite;
        const satellite = dummyTournament ?
            {
                ...satelliteConfiguration.satellite,
                mustPlay: false,
                returnAmount: null,
                tickets: [],
            } : {
                ...satelliteConfiguration.satellite,
                mustPlay: satelliteToDummy || mustPlay,
            };
        const target = !satelliteConfiguration.isTarget ?
            {
                ...satelliteConfiguration.target,
                allowsDirectBuyIn: false,
                tickets: [],
            } : {
                ...satelliteConfiguration.target,
            };

        return {
            ...satelliteConfiguration,
            isSatellite: dummyTournament ? false : satelliteConfiguration.isSatellite,
            satellite,
            target,
        };
    }

    getConfiguration(formModel: ScheduledTournamentConfig): TournamentConfiguration {
        const { currency, dummyTournament, satelliteToDummy } = formModel.configuration;
        const { isTarget } = formModel.satelliteConfiguration;
        const isUpdatedSatelliteToDummy = dummyTournament ? false : satelliteToDummy;
        const isUpdatedDummyTournament = (isUpdatedSatelliteToDummy || !isTarget) ? false : dummyTournament;
        return {
                ...formModel.configuration,
                currency: (satelliteToDummy || dummyTournament) ? 'XSD' : currency,
                dummyTournament: isUpdatedDummyTournament,
                satelliteToDummy: isUpdatedSatelliteToDummy,

        };
    }

    getPrizeBoostConfiguration(formModel: ScheduledTournamentConfig): PrizeBoostConfiguration {
        const { guaranteedPrizePool } = formModel.configuration;
        const { boostCash, enabled } = formModel.prizeBoostConfiguration;
        return {
            ...formModel.prizeBoostConfiguration,
            enabled: guaranteedPrizePool <= 0 && enabled,
            boostCash: guaranteedPrizePool > 0 ? 0 : boostCash,
        };
    }

    formDidUpdate(formModel: ScheduledTournamentConfig, validationMap: any) {
        const newForm = {
            ...formModel,
            configuration: this.getConfiguration(formModel),
            satelliteConfiguration: this.getSatelliteConfiguration(formModel),
            prizeBoostConfiguration: this.getPrizeBoostConfiguration(formModel),
        };
        this.setState({tournament: newForm, validationMap});
    }

    getTitle(): string {
        return 'Scheduled Tournament';
    }

    restore = () => {
        this.tournamentService
            .restore(this.state.tournament.tournamentTemplateId)
            .then(tournament => this.setState({tournament}));
    }

    archive = () => {
        this.tournamentService
            .archive(this.state.tournament.tournamentTemplateId)
            .then(tournament => this.setState({tournament}));
    }

    renderContent() {

        return (
            <div>
                <div className='row wrapper border-bottom white-bg page-heading'>
                    <div className='col-lg-12'>
                        <h1>{this.getTitle()}</h1>
                    </div>
                </div>
                <div className='wrapper wrapper-content animated'>
                    <ScheduledTournamentForm
                        tournament={this.state.tournament}
                        constraints={this.state.constraints}
                        validationMap={this.state.validationMap}
                        timeZones={this.state.timeZones}
                        newTournament={this.props.params.id === 'new'}
                        archive={this.archive}
                        restore={this.restore}
                        formListener={this}
                        onSuccessClose={this.onTournamentSaveConfirmed}
                        calculateSchedule={this.calculateSchedule}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/ui/view/poker/tournament/ScheduledTournamentView.tsx